import { useState } from 'react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Skeleton } from '@/components/atoms/skeleton';
import { PaymentDetailPanel } from '@/components/organisms/payments/PaymentDetailPanel';
import { RefundDialog } from '@/components/organisms/payments/RefundDialog';
import { usePayment } from '@/hooks/payments/usePayment';
import { useRefundPayment } from '@/hooks/payments/useRefundPayment';
import { useIsFieldHidden } from '@/hooks/usePermissions';
import type { PaymentRecord } from '@/hooks/payments/usePayments';

export const Route = createFileRoute('/_dashboard/dashboard/payments/$id')({
  component: PaymentDetailRoute,
});

function PaymentDetailRoute() {
  const { id } = Route.useParams();
  const router = useRouter();
  const pricingHidden = useIsFieldHidden('PRICING');
  const paymentInfoHidden = useIsFieldHidden('PAYMENT_INFO');
  const { data: payment, isLoading, isError, error } = usePayment(id);
  const refund = useRefundPayment();
  const [refundTarget, setRefundTarget] = useState<PaymentRecord | null>(null);

  function handleManageInStripe(target: PaymentRecord) {
    const stripePayment = target.gatewayTxnId ?? target.transactionId;
    const status = target.status.toUpperCase();
    const tab =
      status === 'DISPUTED' ||
      status === 'DISPUTE_UNDER_REVIEW' ||
      status === 'CHARGEBACK_LOST' ||
      status === 'CHARGEBACK_WON'
        ? 'disputes'
        : 'payments';

    void router.navigate({
      to: '/dashboard/stripe',
      search: {
        tab,
        payment: stripePayment ?? null,
      },
    });
  }

  function handleRefundConfirm({
    reason,
    amount,
    refundAsCredit,
    idempotencyKey,
  }: {
    reason: string;
    amount?: number;
    refundAsCredit: boolean;
    idempotencyKey: string;
  }) {
    if (!refundTarget) return;
    refund.mutate(
      {
        id: refundTarget.id,
        invoiceId: refundTarget.invoiceId,
        contactId: refundTarget.contactId,
        jobId: refundTarget.jobId,
        reason: reason || undefined,
        amount,
        gatewayProvider: refundTarget.gatewayProvider,
        refundedAmount: refundTarget.refundedAmount,
        refundAsCredit,
        idempotencyKey,
      },
      {
        onSuccess: () => setRefundTarget(null),
      },
    );
  }

  if (isLoading) {
    return (
      <div className="h-full min-h-0 p-5">
        <Skeleton className="h-7 w-52" />
        <Skeleton className="mt-2 h-4 w-36" />
        <Skeleton className="mt-8 h-64 w-full" />
      </div>
    );
  }

  if (isError || !payment) {
    return (
      <div className="page-enter mx-auto max-w-3xl space-y-4 p-4 sm:p-6">
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          {error instanceof Error ? error.message : 'Payment not found'}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            router.navigate({ to: '/dashboard/payments', search: (prev) => prev as never })
          }
        >
          <ArrowLeft className="mr-1 size-4" />
          Back to Payments
        </Button>
      </div>
    );
  }

  if (pricingHidden || paymentInfoHidden) {
    return (
      <div className="page-enter mx-auto max-w-3xl space-y-4 p-4 sm:p-6">
        <div className="rounded-lg border bg-muted/40 p-4">
          <p className="font-medium">Payment details are hidden for your role.</p>
          <p className="mt-1 text-sm text-muted-foreground">
            You can still review payment status from the Payments list or the related invoice.
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            router.navigate({ to: '/dashboard/payments', search: (prev) => prev as never })
          }
        >
          <ArrowLeft className="mr-1 size-4" />
          Back to Payments
        </Button>
      </div>
    );
  }

  return (
    <>
      <PaymentDetailPanel
        payment={payment}
        onRefund={setRefundTarget}
        onManageInStripe={() => handleManageInStripe(payment)}
      />

      {refundTarget && (
        <RefundDialog
          payment={refundTarget}
          onClose={() => setRefundTarget(null)}
          onConfirm={handleRefundConfirm}
          isPending={refund.isPending}
        />
      )}
    </>
  );
}
